import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAiEditorCache } from './use-ai-editor-cache';
import { AI_EDITOR_ERROR_TEXTS, getAiEditorErrorKey } from '../utils';
import { AiConstantsItem, AiEditorProps } from '../ai-editor.types';
import { message } from 'components/ui';

export const useAiEditor = (props: AiEditorProps) => {
  const { open, modes, onGenerate, onApply, originalText, entityType } = props;

  const onGenerateRef = useRef(onGenerate);
  onGenerateRef.current = onGenerate;

  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;

  const { getResult, setResult, removeResult, clearResults } =
    useAiEditorCache();

  const [currentMode, setCurrentMode] = useState<string | null>(
    modes[0]?.value ?? null
  );
  const [currentStyle, setCurrentStyle] = useState<string | null>(null);
  const [addEmoji, setAddEmoji] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [result, setResultState] = useState<string | null>(null);

  const activeMode = useMemo(
    () => modes.find((m) => m.value === currentMode) ?? null,
    [modes, currentMode]
  );

  const showAddEmojiOption = useMemo(
    () => currentMode === 'stylization' && entityType === 'news',
    [currentMode, entityType]
  );

  const hasStyles = (activeMode?.children?.length ?? 0) > 0;
  const activeKey = hasStyles ? currentStyle : currentMode;

  useEffect(() => {
    if (open) {
      setCurrentMode(modes[0]?.value ?? null);
      setCurrentStyle(null);
      setAddEmoji(false);
    }

    return () => {
      clearResults();
      setResultState(null);
    };
  }, [open, clearResults, modes]);

  const handleModeSelect = useCallback(
    (mode: AiConstantsItem) => {
      const newStyle = mode.children?.[0]?.value ?? null;
      const modeHasStyles = (mode.children?.length ?? 0) > 0;

      const newKey = modeHasStyles
        ? addEmoji
          ? `${newStyle}_withEmoji`
          : newStyle
        : mode.value;

      setCurrentMode(mode.value);
      setCurrentStyle(null);
      setResultState(newKey ? (getResult(newKey) ?? null) : null);
    },
    [getResult, addEmoji]
  );

  const handleStyleSelect = useCallback(
    (value: string) => {
      setCurrentStyle(value);

      const key = addEmoji ? `${value}_withEmoji` : value;
      setResultState(getResult(key) ?? null);
    },
    [getResult, addEmoji]
  );

  const handleAddEmoji = useCallback(
    (hasEmoji: boolean) => {
      setAddEmoji(hasEmoji);

      if (!activeKey) {
        return;
      }

      const key = hasEmoji ? `${activeKey}_withEmoji` : activeKey;
      setResultState(getResult(key) ?? null);
    },
    [getResult, activeKey]
  );

  const handleGenerate = useCallback(async () => {
    if (!activeKey) {
      return message({
        type: 'error',
        text: 'Выберите стиль, чтобы начать генерацию',
      });
    }

    setIsGenerating(true);

    try {
      const response = await onGenerateRef.current({
        text: originalText,
        style: activeKey,
        ...(showAddEmojiOption && { add_emoji: addEmoji }),
      });

      // Пустой или нечитаемый ответ от ИИ
      if (!response || typeof response !== 'string' || !response.trim()) {
        message({ type: 'error', text: AI_EDITOR_ERROR_TEXTS.empty });

        return;
      }

      const key = addEmoji ? `${activeKey}_withEmoji` : activeKey;

      setResult(key, response);
      setResultState(response);
    } catch (error: any) {
      const errorText = AI_EDITOR_ERROR_TEXTS[getAiEditorErrorKey(error)];

      message({ type: 'error', text: errorText });
    } finally {
      setIsGenerating(false);
    }
  }, [activeKey, originalText, setResult, addEmoji, showAddEmojiOption]);

  const handleCancel = useCallback(() => {
    if (!activeKey) {
      return;
    }

    const key = addEmoji ? `${activeKey}_withEmoji` : activeKey;

    removeResult(key);
    setResultState(null);
  }, [activeKey, removeResult]);

  const handleApply = useCallback(() => {
    if (!result) {
      return;
    }

    onApplyRef.current?.(result);
  }, [result]);

  return {
    modes,
    activeMode,
    currentStyle,
    hasStyles,
    showAddEmojiOption,
    addEmoji,
    replaceOnResult: hasStyles,
    isGenerating,
    originalText,
    result,
    handleModeSelect,
    handleStyleSelect,
    handleAddEmoji,
    handleGenerate,
    handleCancel,
    handleApply,
  };
};
